import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, fmtMoney, fmtRoas, minsToHM } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { KPICard, StageBadge, VerdictBadge, Spinner, SectionTitle, EmptyState, roasTone, PriorityChip } from '@/components/common';
import { ProductDialog } from '@/components/ProductDialog';
import { toast } from 'sonner';
import { ArrowRight, Zap, Sparkles, Flame, AlertTriangle, Plus, CalendarCheck, Target } from 'lucide-react';

export default function CommandCenter() {
  const [data, setData] = useState(null);
  const [aiNba, setAiNba] = useState(null);
  const [nbaBusy, setNbaBusy] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const { stagesData } = useApp();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/dashboard');
      setData(data);
      if (data.latest_weekly_review) setReview(data.latest_weekly_review.content);
    } catch (e) {
      // 401 handled globally by the api interceptor; log anything else
      if (e.response?.status !== 401) console.error('Failed to load dashboard', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const askAI = async () => {
    setNbaBusy(true);
    try {
      const { data } = await api.post('/ai/next-action');
      setAiNba(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI next-action failed');
    } finally { setNbaBusy(false); }
  };

  const genReview = async () => {
    setReviewBusy(true);
    try {
      const { data } = await api.post('/ai/weekly-review');
      setReview(data);
      setReviewOpen(true);
      toast.success('Weekly review generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Weekly review failed');
    } finally { setReviewBusy(false); }
  };

  const toggleTask = async (t) => {
    await api.put(`/tasks/${t.id}`, { done: !t.done });
    load();
  };

  if (!data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-36 w-full" />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const nba = aiNba || data.next_best_action;
  const pnl = data.today_pnl;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Command Center</h1>
          <div className="page-accent-bar" />
          <p className="mt-2 text-sm text-muted-foreground">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · {data.product_count} products in pipeline</p>
        </div>
        <ProductDialog onCreated={(p) => navigate(`/products/${p.id}`)} trigger={
          <Button data-testid="command-center-add-product-button" className="accent-btn gap-2"><Plus className="h-4 w-4" /> Add Product</Button>
        } />
      </div>

      {/* Next Best Action hero */}
      <Card data-testid="next-best-action-card" className="relative overflow-hidden border-primary/30 shadow-sm">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(900px circle at 15% 0%, hsl(var(--primary) / 0.12), transparent 55%)' }} />
        <CardContent className="relative p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary">
            <Zap className="h-3.5 w-3.5" /> Next Best Action {aiNba && <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px]">AI</span>}
          </div>
          <p className="mt-2 text-lg font-semibold leading-snug sm:text-xl">{nba.action}</p>
          <p data-testid="next-best-action-rationale" className="mt-1 text-sm text-muted-foreground">{nba.why}</p>
          {nba.expected_outcome && <p className="mt-1 text-xs text-muted-foreground"><Target className="mr-1 inline h-3 w-3" />{nba.expected_outcome}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            {nba.link && (
              <Button data-testid="next-best-action-primary-button" size="sm" onClick={() => navigate(nba.link)} className="accent-btn gap-1">
                Start <ArrowRight className="h-4 w-4" />
              </Button>
            )}
            <Button data-testid="next-best-action-ask-ai-button" size="sm" variant="outline" onClick={askAI} disabled={nbaBusy} className="gap-2">
              {nbaBusy ? <Spinner /> : <Sparkles className="h-4 w-4" />} Ask AI for deeper call
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Signals */}
      {data.signals?.length > 0 && (
        <div className="space-y-2">
          {data.signals.map((s) => (
            <div key={`${s.product}:${s.signal}`} data-testid="metric-signal-alert" className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm backdrop-blur-md">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <span><strong>{s.product}:</strong> {s.signal}</span>
            </div>
          ))}
        </div>
      )}

      {/* Today PnL */}
      <div>
        <SectionTitle>Today's PnL</SectionTitle>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <KPICard testId="kpi-today-revenue" label="Revenue" value={fmtMoney(pnl.revenue)} />
          <KPICard testId="kpi-today-spend" label="Ad Spend" value={fmtMoney(pnl.spend)} />
          <KPICard testId="kpi-today-profit" label="Profit" value={fmtMoney(pnl.profit)} tone={pnl.profit > 0 ? 'good' : pnl.profit < 0 ? 'bad' : undefined} />
          <KPICard testId="kpi-today-roas" label="ROAS" value={fmtRoas(pnl.roas)} tone={pnl.roas >= 2 ? 'good' : pnl.spend > 0 && pnl.roas < 1 ? 'bad' : undefined} />
          <KPICard testId="kpi-today-purchases" label="Purchases" value={pnl.purchases} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Active tests */}
          <div>
            <SectionTitle right={<Link to="/pipeline" className="text-xs text-primary hover:underline">View pipeline →</Link>}>Active Tests</SectionTitle>
            {data.active_tests.length === 0 ? (
              <EmptyState testId="active-tests-empty" icon={Flame} title="No live tests right now"
                subtitle="Move a product to Live Testing when your ads start spending, and it will show up here with daily numbers." />
            ) : (
              <Card>
                <Table data-testid="active-tests-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead className="text-right">Last Spend</TableHead>
                      <TableHead className="text-right">ROAS</TableHead>
                      <TableHead className="text-right">Profit</TableHead>
                      <TableHead>Next Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.active_tests.map(({ product: p, latest_metric: m }) => (
                      <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/products/${p.id}`)}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell><StageBadge stage={p.stage} stages={stagesData.stages} /></TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-sm">{m ? fmtMoney(m.spend) : '—'}</TableCell>
                        <TableCell className={`text-right font-mono tabular-nums text-sm ${m?.computed ? roasTone(m.computed.roas, m.spend) : ''}`}>{m?.computed ? fmtRoas(m.computed.roas) : '—'}</TableCell>
                        <TableCell className={`text-right font-mono tabular-nums text-sm ${m?.computed?.profit > 0 ? 'text-emerald-600 dark:text-emerald-400' : m?.computed?.profit < 0 ? 'text-rose-600 dark:text-rose-400' : ''}`}>{m?.computed ? fmtMoney(m.computed.profit) : '—'}</TableCell>
                        <TableCell className="max-w-[220px] truncate text-xs text-muted-foreground">{p.next_action || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>

          {/* Tasks */}
          <div>
            <SectionTitle right={<Link to="/tasks" className="text-xs text-primary hover:underline">All tasks →</Link>}>Today's Tasks</SectionTitle>
            {data.open_tasks.length === 0 ? (
              <EmptyState testId="dashboard-tasks-empty" icon={CalendarCheck} title="No open tasks" subtitle="Add tasks from the Tasks page or let stage automations create them for you." />
            ) : (
              <Card>
                <CardContent className="divide-y p-0">
                  {data.open_tasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                      <Checkbox data-testid="dashboard-task-checkbox" checked={t.done} onCheckedChange={() => toggleTask(t)} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{t.title}</p>
                        <p className="text-xs text-muted-foreground">{t.category}{t.due_date ? ` · due ${t.due_date}` : ''}</p>
                      </div>
                      {!t.done && <PriorityChip priority={t.priority || 3} />}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Right rail */}
        <div className="space-y-6">
          <Card data-testid="dashboard-time-card">
            <CardContent className="p-5">
              <SectionTitle>Consistency</SectionTitle>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="font-mono text-xl font-bold tabular-nums">{minsToHM(data.time.today_minutes)}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Today</p>
                </div>
                <div>
                  <p className="font-mono text-xl font-bold tabular-nums">{data.time.streak}<span className="text-sm">d</span></p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Streak</p>
                </div>
                <div>
                  <p className="font-mono text-xl font-bold tabular-nums">{data.time.consistency_score}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Score /100</p>
                </div>
              </div>
              <div className="mt-4 flex items-end gap-1" aria-label="Last 7 days work minutes">
                {data.time.week_days.map((d) => (
                  <div key={d.date} className="flex-1">
                    <div className="mx-auto w-full rounded-sm bg-orange-400/75" style={{ height: `${Math.min(48, Math.max(3, (d.minutes / 240) * 48))}px` }} title={`${d.date}: ${minsToHM(d.minutes)}`} />
                  </div>
                ))}
              </div>
              <Link to="/time"><Button variant="outline" size="sm" className="mt-4 w-full">Time Tracker</Button></Link>
            </CardContent>
          </Card>

          <Card data-testid="dashboard-pipeline-card">
            <CardContent className="p-5">
              <SectionTitle>Pipeline Snapshot</SectionTitle>
              <div className="space-y-1.5">
                {(stagesData.stages || []).map((s) => {
                  const count = data.stage_counts[s.key] || 0;
                  if (count === 0) return null;
                  return (
                    <div key={s.key} className="flex items-center justify-between text-sm">
                      <StageBadge stage={s.key} stages={stagesData.stages} />
                      <span className="font-mono tabular-nums">{count}</span>
                    </div>
                  );
                })}
                {Object.keys(data.stage_counts).length === 0 && <p className="text-xs text-muted-foreground">Pipeline is empty. Add your first product idea.</p>}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="dashboard-weekly-review-card">
            <CardContent className="p-5">
              <SectionTitle>Weekly Review</SectionTitle>
              {review ? (
                <>
                  <p className="text-sm font-medium">{review.headline}</p>
                  <Button data-testid="weekly-review-view-button" variant="outline" size="sm" className="mt-3 w-full" onClick={() => setReviewOpen(true)}>Read full review</Button>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Generate an AI review of your week: PnL, time, product calls and next week's focus.</p>
              )}
              <Button data-testid="weekly-review-generate-button" size="sm" className="mt-2 w-full gap-2" onClick={genReview} disabled={reviewBusy}>
                {reviewBusy ? <Spinner /> : <Sparkles className="h-4 w-4" />} {review ? 'Regenerate' : 'Generate'} Weekly Review
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Weekly review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>Weekly Review</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-4 text-sm">
              <p className="text-base font-semibold">{review.headline}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Wins</p>
                  <ul className="list-disc space-y-1 pl-4">{(review.wins || []).map((w) => <li key={w}>{w}</li>)}</ul>
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">Problems</p>
                  <ul className="list-disc space-y-1 pl-4">{(review.problems || []).map((w) => <li key={w}>{w}</li>)}</ul>
                </div>
              </div>
              <div><p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">PnL Analysis</p><p>{review.pnl_analysis}</p></div>
              <div><p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Time Analysis</p><p>{review.time_analysis}</p></div>
              {review.product_calls?.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Product Calls</p>
                  <div className="space-y-2">
                    {review.product_calls.map((c) => (
                      <div key={`${c.product}:${c.call}`} className="rounded-lg border bg-muted/40 p-3">
                        <p className="font-medium">{c.product} — <span className="text-primary">{c.call}</span></p>
                        <p className="text-xs text-muted-foreground">{c.why}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Focus Next Week</p>
                <ol className="list-decimal space-y-1 pl-4">{(review.focus_next_week || []).map((f) => <li key={f}>{f}</li>)}</ol>
              </div>
              <p className="text-xs text-muted-foreground">{review.consistency_note}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

